import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import { Toaster } from "sonner";
import ProtectedRoutes from "./components/ProtectedRoutes";
import Unauthorized from "./components/display/Unauthorized";
import { ALL_ADMIN_ROLES, PERMISSIONS } from "./lib/rbac";
import DeliveryMissingReportPage from "./components/display/deliveryNote/DeliveryMissingReportPage";
import ManageQuotations from "./components/display/quotation/ManageQuotation";
import Products from "./viewerComponents/Products";

const CompanyForm = lazy(() => import("./components/Form/CompanyForm"));
const ProductForm = lazy(() => import("./components/Form/ProductForm"));
const MainLayout = lazy(() => import("./components/layouts/MainLayout"));
const About = lazy(() => import("./viewerComponents/About"));
const ContactUs = lazy(() => import("./viewerComponents/ContactUs"));
const Home = lazy(() => import("./viewerComponents/Home"));
const Inbox = lazy(() => import("./components/mails/inbox"));
const ShowInvoices = lazy(() => import("./components/display/ShowInvoices"));
const AddUser = lazy(() => import("./components/Form/AddUser"));
const LoginForm = lazy(() => import("./components/Form/LoginForm"));
const NotFound = lazy(() => import("./components/display/NotFound"));
const AdminHome = lazy(() => import("./components/home/AdminHome"));
const ShowExpenses = lazy(() => import("./components/display/expense/Showexpenses"));
const ServerReportList = lazy(() => import("./components/display/server/ServerReportList"));
const DailyServerReport = lazy(() => import("./components/display/server/DailyServerReport"));
const ShowWifiBoost = lazy(() => import("./components/display/wifi/ShowWifiBoost"));
const ShowPurchaseBills = lazy(() => import("./components/display/purchase/ShowPurchaseBills"));
const Dashboard = lazy(() => import("./components/display/Dashboard"));
const ShowSiteWork = lazy(() => import("./components/display/workType/ShowSiteWork"));
const ManageEmployees = lazy(() => import("./components/display/employees/ManageEmployees"));
const ManageCompanies = lazy(() => import("./components/display/company/ManageCompanies"));
const ManageProducts = lazy(() => import("./components/display/products/ManageProducts"));
const ManageProductSection = lazy(() => import("./components/display/productSection/ManageProductSection"));
const ManageBannerSlides = lazy(() => import("./components/display/carousels/ManageBannerSlides"));
const ManageWifiOptions = lazy(() => import("./components/display/wifiOptions/ManageWifiOptions"));
const StockManager = lazy(() => import("./components/display/stock/StockManager"));
const ManageUserCreations = lazy(() => import("./components/display/userCreation/ManageUserCreations"));
const ManageProductReceipts = lazy(() => import("./components/display/productReceipt/ManageProductReceipts"));
const ManageDeliveryNotes = lazy(() => import("./components/display/deliveryNote/ManageDeliveryNotes"));
const ManageServiceCallReports = lazy(() => import("./components/display/serviceCallReport/ManageServiceCallReports"));
const ManageSystemIps = lazy(() => import("./components/display/systemIp/ManageSystemIps"));
const ManageInchargeDetails = lazy(() => import("./components/display/inchargeDetails/ManageInchargeDetails"));
const ManageCamFireSystems = lazy(() => import("./components/display/camFireSystem/ManageCamFireSystems"));
const DeliveryNoteConfirmation = lazy(() => import("./viewerComponents/DeliveryNoteConfirmation"));

const APP_TITLE = "Gnana Syndicate";

const ROUTE_TITLES = {
  "/admin/dashboard": "Dashboard",
  "/admin/employee/manage": "Manage Employee",
  "/admin/add/user": "Add User",
  "/admin/add/product": "Add Product",
  "/admin/add/company": "Add Company",
  "/admin/company/manage": "Manage Companies",
  "/admin/products/manage": "Manage Products",
  "/admin/productSection/manage": "Products Section",
  "/admin/bannerSlide/manage": "Banner Section",
  "/admin/show/invoices": "View Invoices",
  "/admin/server": "Server Report",
  "/admin/server/daily": "Daily Server Report",
  "/admin/expense": "Expense Voucher",
  "/admin/purchase-bill": "Purchase Bill",
  "/admin/site-work": "Site Work",
  "/admin/mail": "Web Mails",
  "/admin/manage/wifi/server/options": "Server / Wifi / Person",
  "/admin/stock": "Stock In/Out",
  "/admin/user-creations": "Unit System User Creation",
  "/admin/product-receipts": "Warranty Receipt / Challan Register",
  "/admin/delivery-notes": "Delivery Note & Challan",
  "/admin/service-call-reports": "Service Call Report",
  "/admin/system-ips": "All Site System & IP Details",
  "/admin/incharge-details": "Unit Name & Incharge details",
  "/admin/cam-fire-systems": "Camera & Fire System Details",
  "/admin/wifi-boost": "Wifi Boost",
  "/admin/home": "Admin Home",
  "/admin/login/onlyAdmins": "Admin Login",
  "/": "Ocellus Advanced Security Solutions",
  "/about": "About Us",
  "/contact": "Contact Us",
  "/admin/cash/quotation": "Cash & Quotations",
};

const INVOICE_BILL_TYPE_TITLES = {
  invoice: "Sales Invoice",
  "sales invoice": "Sales Invoice",
  proforma: "Proforma Invoice",
  performa: "Proforma Invoice",
  material: "Cash Bill",
  "cash bill": "Cash Bill",
  credit: "Credit Note",
  "credit note": "Credit Note",
  quotation: "Quotation",
};

const getPageTitle = (location) => {
  if (location.pathname === "/admin/create/invoice") {
    const params = new URLSearchParams(location.search);
    const billType = String(params.get("billType") || "").trim().toLowerCase();
    return INVOICE_BILL_TYPE_TITLES[billType] || "Sales Invoice";
  }

  return ROUTE_TITLES[location.pathname] || APP_TITLE;
};

const PageTitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = getPageTitle(location);
  }, [location]);

  return null;
};
const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
    Loading...
  </div>
);

function App() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <PageTitleUpdater />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<ProtectedRoutes allowedRoles={PERMISSIONS.DASHBOARD} />}>
              <Route path="/admin/dashboard" element={<Dashboard />}></Route>
            </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.EMPLOYEE_MANAGE} />}
          >
            <Route
              path="/admin/employee/manage"
              element={<ManageEmployees />}
            />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.USER_ADD} />}
          >
            <Route path="/admin/add/user" element={<AddUser />} />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.MASTER_DATA_MANAGE} />}
          >
            <Route path="/admin/add/product" element={<ProductForm />}></Route>
            <Route path="/admin/add/company" element={<CompanyForm />}></Route>
            <Route path="/admin/company/manage" element={<ManageCompanies />} />
            <Route path="/admin/products/manage" element={<ManageProducts />} />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.PRODUCT_SECTION_MANAGE} />}
          >
            <Route
              path="/admin/productSection/manage"
              element={<ManageProductSection />}
            />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.BANNER_MANAGE} />}
          >
            <Route
              path="/admin/bannerSlide/manage"
              element={<ManageBannerSlides />}
            />
          </Route>
          <Route
            element={
              <ProtectedRoutes allowedRoles={PERMISSIONS.FINANCE} />
            }
          >
            <Route
              path="/admin/show/invoices"
              element={<ShowInvoices />}
            ></Route>
            <Route path="/admin/cash/quotation" element={<ManageQuotations />}></Route>
            <Route path="/admin/create/invoice" element={<MainLayout />} />
          </Route>

          <Route
            element={
              <ProtectedRoutes
                allowedRoles={PERMISSIONS.SERVER_REPORT}
              />
            }
          >
            <Route path="/admin/server" element={<ServerReportList />}></Route>
            <Route
              path="/admin/server/daily"
              element={<DailyServerReport />}
            ></Route>
          </Route>
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={PERMISSIONS.OPERATIONS}
              />
            }
          >
            <Route path="/admin/expense" element={<ShowExpenses />}></Route>
            <Route
              path="/admin/purchase-bill"
              element={<ShowPurchaseBills />}
            ></Route>
            <Route path="/admin/site-work" element={<ShowSiteWork />}></Route>
            <Route path="/admin/mail" element={<Inbox />} />
            <Route path={"/admin/manage/wifi/server/options"} element={<ManageWifiOptions />} />
          </Route>

          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.USER_CREATION} />}
          >
            <Route path="/admin/stock" element={<StockManager />} />
            <Route path="/admin/user-creations" element={<ManageUserCreations />} />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.PRODUCT_RECEIPTS} />}
          >
            <Route path="/admin/product-receipts" element={<ManageProductReceipts />} />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.DELIVERY_NOTES} />}
          >
            <Route path="/admin/delivery-notes" element={<ManageDeliveryNotes />} />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.SERVICE_CALL_REPORTS} />}
          >
            <Route path="/admin/service-call-reports" element={<ManageServiceCallReports />} />
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={PERMISSIONS.SYSTEM_RECORDS} />}
          >
            <Route path="/admin/system-ips" element={<ManageSystemIps />} />
            <Route path="/admin/incharge-details" element={<ManageInchargeDetails />} />
            <Route path="/admin/cam-fire-systems" element={<ManageCamFireSystems />} />
          </Route>
          <Route
            element={
              <ProtectedRoutes
                allowedRoles={PERMISSIONS.WIFI_BOOST}
              />
            }
          >
            <Route path="/admin/wifi-boost" element={<ShowWifiBoost />}></Route>
          </Route>
          <Route
            element={<ProtectedRoutes allowedRoles={ALL_ADMIN_ROLES} />}
          >
            <Route path="/admin/home" element={<AdminHome />}></Route>
          </Route>

          <Route path="/admin/login/onlyAdmins" element={<LoginForm />}></Route>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/products" element={<Products />} />
          <Route path="/delivery-confirm/:token" element={<DeliveryNoteConfirmation />} />
          <Route path="/delivery-missing-report/:token" element={<DeliveryMissingReportPage />} />
          <Route path="/admin" element={<Unauthorized />}></Route>
            <Route path="*" element={<NotFound />}></Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
